import React, { useMemo } from 'react';
import { InputAdornment, IconButton, MenuItem } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { es as esLocale } from 'date-fns/locale';
import { format, isValid, parse } from 'date-fns';
import { InventoryFilter } from '../../../types/inventory.types';
import CatalogAutocomplete from '../../common/CatalogAutocomplete';
import { useInventoryFilters } from './InventoryFilters.hooks';
import {
  ListPageFilterContainer as FilterContainer,
  ListPageEqualSecondaryFilterRow as SecondaryFilterRow,
  ListPageFilterTextField as StyledTextField,
  ListPageFilterCollapse as FilterCollapse,
  ListPagePrimaryFilterRow,
  ListPageActiveFilters,
} from '../../common/list-page';
import { useDataVisibilityPermissions } from '../../../hooks/useDataVisibilityPermissions';

export interface InventoryFiltersProps {
  filters: InventoryFilter;
  setFilters: React.Dispatch<React.SetStateAction<InventoryFilter>>;
  appliedFilters: InventoryFilter;
  onClearAppliedField: (field: keyof InventoryFilter) => void;
  onSearch: (nextFilters?: InventoryFilter) => void;
  onReset: () => void;
  suppliers?: Array<{ id: number; name: string }>;
}

const InventoryFilters: React.FC<InventoryFiltersProps> = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  onSearch,
  onReset,
  suppliers = [],
}) => {
  const { canShowSupplierFilter } = useDataVisibilityPermissions();
  const referenceDate = useMemo(() => new Date(), []);

  // Usar el hook personalizado para toda la lógica
  const {
    expandedFilters,
    quickSearch,
    setQuickSearch,
    handleSelectChange,
    handleDateChange,
    toggleFilters,
    handleQuickSearchChange,
    handleQuickSearch,
    handleQuickSearchKeyPress,
    handleClearSearch,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hasActiveFilters,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    activeFiltersCount,
    activeFilterChips,
  } = useInventoryFilters({
    filters,
    setFilters,
    appliedFilters,
    onClearAppliedField,
    onSearch,
    onReset,
    suppliers,
  });

  const parseFilterDate = (iso: string): Date | null => {
    if (!iso?.trim()) return null;
    const d = parse(iso, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : null;
  };

  const datePickerSlotProps = useMemo(
    () => ({
      textField: {
        fullWidth: true as const,
        variant: 'outlined' as const,
        size: 'medium' as const,
        InputLabelProps: { shrink: true },
      },
      field: { clearable: true },
    }),
    [],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
      <FilterContainer>
        <ListPagePrimaryFilterRow
          showMoreFilters={expandedFilters}
          onToggleMoreFilters={toggleFilters}
          onReset={handleClearSearch}
          onSearch={handleQuickSearch}
          searchFieldProps={{
            label: 'Buscar Registros',
            value: quickSearch,
            onChange: handleQuickSearchChange,
            onKeyDown: handleQuickSearchKeyPress,
            InputProps: {
              endAdornment: quickSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setQuickSearch('')}
                    edge="end"
                    aria-label="clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />

        {/* Segunda fila - Filtros adicionales */}
        <FilterCollapse in={expandedFilters} timeout={350} unmountOnExit>
          <SecondaryFilterRow>
            {/* Fecha Inicio */}
            <DatePicker
              label="Fecha Inicio"
              format="dd/MM/yyyy"
              value={parseFilterDate(filters.startDate || '')}
              onChange={(newDate) => {
                handleDateChange(
                  'startDate',
                  newDate && isValid(newDate) ? format(newDate, 'yyyy-MM-dd') : '',
                );
              }}
              views={['year', 'month', 'day']}
              openTo="day"
              yearsPerRow={4}
              referenceDate={referenceDate}
              slotProps={datePickerSlotProps}
            />

            {/* Fecha Fin */}
            <DatePicker
              label="Fecha Fin"
              format="dd/MM/yyyy"
              value={parseFilterDate(filters.endDate || '')}
              onChange={(newDate) => {
                handleDateChange(
                  'endDate',
                  newDate && isValid(newDate) ? format(newDate, 'yyyy-MM-dd') : '',
                );
              }}
              views={['year', 'month', 'day']}
              openTo="day"
              yearsPerRow={4}
              referenceDate={referenceDate}
              slotProps={datePickerSlotProps}
            />

            {/* Proveedor */}
            {canShowSupplierFilter && (
              <CatalogAutocomplete
                catalogType="activeSupplier"
                value={filters.supplierId ?? ''}
                onChange={(value) => setFilters((prev) => ({ ...prev, supplierId: value }))}
                pinOptions={suppliers}
                label="Proveedor"
                size="medium"
                textFieldProps={{ variant: 'outlined', fullWidth: true }}
              />
            )}

            {/* Estado */}
            <StyledTextField
              fullWidth
              select
              variant="outlined"
              size="medium"
              label="Estado"
              name="recordStatus"
              value={filters.recordStatus}
              onChange={handleSelectChange as any}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              <MenuItem value="completed">Completado</MenuItem>
              <MenuItem value="pending">Pendiente</MenuItem>
              <MenuItem value="cancelled">Cancelado</MenuItem>
            </StyledTextField>
          </SecondaryFilterRow>
        </FilterCollapse>

        <ListPageActiveFilters chips={activeFilterChips} />
      </FilterContainer>
    </LocalizationProvider>
  );
};

export default InventoryFilters;
